<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import IconClipboard from '@/components/icons/IconClipboard.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import { useAlert } from '@/libs/alertPortal'
import { useFetchSettingsPageLineActivationInflowSourceDefinitionViewQuery } from './SettingsPageLineActivationInflowSourceView.generated'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import { useRouter, useRoute } from 'vue-router'
import { getAPIBaseURL } from '@/config'
import { useModal } from '@/hooks/useModal'
import QrCodeModal from '@/pages/SettingsPage/SettingsPageLineActivationInflowSourceView/QrCodeModal.vue'

const alert = useAlert()
const router = useRouter()

const route = useRoute('LineActivationSettingInflowSource')
const entranceGUID = route.params.entranceGUID

const { ControlledModal, openModal, closeModal } = useModal()

const inflowSourceUrl = ref('')
const qrUrl = computed(() => {
  if (!inflowSourceUrl.value) return ''
  return `${getAPIBaseURL('qr')}/?${new URLSearchParams({ m: inflowSourceUrl.value, normal: '' })}`
})

const copyURL = async (url: string) => {
  await navigator.clipboard.writeText(url)

  alert.success('URL をコピーしました')
}

const openQRCodeModal = (url: string) => {
  inflowSourceUrl.value = url
  openModal()
}

const { result, loading } =
  useFetchSettingsPageLineActivationInflowSourceDefinitionViewQuery({
    fetchPolicy: 'no-cache',
  })

const inflowSourceDefinitions = computed(
  () => result.value?.userRegistrationInflowSourceDefinitions
)

watch(loading, () => {
  if (!result.value?.lineActivationProviderLiffAppConfig) {
    alert.error(
      '友だち追加経路を利用するにはLINEログインチャンネルおよびLIFF IDの設定が必要です。'
    )

    // eslint-disable-next-line fp/no-mutating-methods
    router.push({
      name: 'settings',
    })
  }
})
</script>

<template>
  <SettingsPagePanel>
    <SettingsPagePanelHeader>
      <template #title>友だち追加経路 一覧</template>
      <template #right>
        <RouterLink :to="{ name: 'LineActivationSettingInflowSourceCreate' }">
          <FlatButton>友だち追加経路を追加する</FlatButton>
        </RouterLink>
      </template>
    </SettingsPagePanelHeader>
    <section v-if="loading" :class="$style.loading">
      <LoadingIndicator />
    </section>
    <section v-else>
      <div
        v-if="inflowSourceDefinitions && inflowSourceDefinitions.length > 0"
        :class="$style.table"
      >
        <div
          v-for="inflowSource in inflowSourceDefinitions"
          :key="inflowSource.guid"
          :class="$style.table__row"
        >
          <RouterLink
            :to="{
              name: 'LineActivationSettingInflowSourceEdit',
              params: {
                inflowSourceId: inflowSource.guid,
                entranceGUID: entranceGUID,
              },
            }"
          >
            <span :class="$style.row__col">{{
              inflowSource.inflowSourceName
            }}</span>
          </RouterLink>
          <div :class="$style.url">
            <span>{{ inflowSource.url }}</span>
            <p :class="$style.count">{{ inflowSource.inflowCount }}件</p>
            <div :class="$style.flex">
              <FlatButton
                variant="plain"
                size="small"
                @click="copyURL(inflowSource.url ?? '')"
              >
                <div :class="$style.copy">
                  <IconClipboard />
                  <span>copy</span>
                </div>
              </FlatButton>
              <div :class="$style.qrArea">
                <FlatButton
                  variant="plain"
                  size="small"
                  @click="openQRCodeModal(inflowSource.url ?? '')"
                >
                  QRコード表示
                </FlatButton>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p v-else>登録されている友だち追加経路がありません</p>
    </section>

    <!-- モーダル -->
    <ControlledModal>
      <QrCodeModal :url="qrUrl" @close="closeModal" />
    </ControlledModal>
  </SettingsPagePanel>
</template>

<style module lang="scss">
.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.table {
  &__row {
    padding: 16px;
    display: grid;
    grid-template-columns: 1fr 4fr;
    gap: 8px;
    align-items: center;

    &:nth-child(n + 2) {
      border-top: 1px solid $color-border-low-emphasis;
    }
  }
}

.url {
  display: grid;
  grid-template-columns: 1fr auto auto;
  justify-content: space-between;
  align-items: center;
}

.copy {
  display: flex;
  align-items: center;
}

.count {
  color: $color-red-600;
  margin: 0 20px;
}

.flex {
  display: flex;
  align-items: center;
}

.qrArea {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
